import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, Globe, Users, Layers, Network, RefreshCw, Loader2, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid, ReferenceLine,
} from "recharts";

const DIMENSIONS = [
  { key: "account", label: "By Ad Account", icon: Layers,
    help: "Compare ROI across the FB ad accounts connected to this workspace." },
  { key: "vertical", label: "By Vertical", icon: Users,
    help: "Which niche (debt, medicare, insurance…) is making you the most money." },
  { key: "network", label: "By Ad Network", icon: Network,
    help: "Facebook vs. Google Ads vs. TikTok rollup (ready for future networks)." },
  { key: "buyer", label: "By Buyer", icon: Users,
    help: "Team leaderboard — who's the top performer this week." },
  { key: "geo", label: "By Geo", icon: Globe,
    help: "Country-level breakdown from FB Insights. Cached 15 min — may take 5-10s on first load." },
];

const fmtMoney = (n) => "$" + (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => (Number(n || 0)).toFixed(1) + "%";
const fmtInt = (n) => (Number(n || 0)).toLocaleString();

function exportCsv(rows, dim) {
  if (!rows?.length) return toast.error("Nothing to export");
  const keys = Object.keys(rows[0]).filter((k) => !Array.isArray(rows[0][k]));
  const csv = [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `report-${dim}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [dim, setDim] = useState("account");
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cacheTtl, setCacheTtl] = useState(null);

  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const url = dim === "geo"
        ? `/reports/geo?days=${days}`
        : `/reports/pivot?dimension=${dim}&days=${days}`;
      const { data } = await api.get(url, { timeout: 60000 });
      setRows(dim === "geo" ? (data.countries || []) : (data.groups || []));
      setCacheTtl(dim === "geo" ? data.cache_ttl_min : null);
    } catch (e) {
      // Soft-fail: 400/404/empty-data cases shouldn't nag the user with a red
      // toast. Inline an error banner instead — less alarming, stays visible.
      const msg = e?.response?.data?.detail || e?.message || "Couldn't load report";
      setLoadError(msg);
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dim, days]);

  const totals = rows.reduce((acc, r) => ({
    spend: acc.spend + (r.spend || 0),
    revenue: acc.revenue + (r.revenue || 0),
    clicks: acc.clicks + (r.clicks || 0),
  }), { spend: 0, revenue: 0, clicks: 0 });
  const blendedRoi = totals.spend > 0 ? ((totals.revenue - totals.spend) / totals.spend * 100) : 0;

  return (
    <div className="p-6 max-w-[1400px]" data-testid="reports-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-600" /> Reports
        </h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Pivoted aggregations across accounts, verticals, networks, buyers, and countries.
        </p>
      </div>

      <GrossProfitChart />
      {/* Dimension tabs */}
      <div className="flex items-center gap-1 mb-3 flex-wrap" data-testid="reports-tabs">
        {DIMENSIONS.map((d) => {
          const Icon = d.icon;
          const active = dim === d.key;
          return (
            <button
              key={d.key}
              onClick={() => setDim(d.key)}
              data-testid={`reports-tab-${d.key}`}
              className={`flex items-center gap-1.5 text-xs font-medium rounded-md px-3 py-2 transition-all ${
                active ? "bg-indigo-600 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
              title={d.help}
            >
              <Icon className="w-3.5 h-3.5" /> {d.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="reports-range"
            className="bg-white border border-slate-200 rounded-md px-2 py-2 text-xs"
          >
            {[1, 3, 7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>Last {d}d</option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="reports-refresh"
            className="flex items-center gap-1 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 rounded-md px-3 py-2 text-xs"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
          <button
            onClick={() => exportCsv(rows, dim)}
            disabled={!rows.length}
            data-testid="reports-export"
            className="flex items-center gap-1 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 rounded-md px-3 py-2 text-xs"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="Total Spend"   value={fmtMoney(totals.spend)}   />
        <Stat label="Total Revenue" value={fmtMoney(totals.revenue)} />
        <Stat label="Total Clicks"  value={fmtInt(totals.clicks)}    />
        <Stat label="Blended ROI"   value={fmtPct(blendedRoi)} positive={blendedRoi > 0} />
      </div>

      {loadError && (
        <div
          data-testid="report-inline-error"
          className="mb-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2"
        >
          <span>⚠</span>
          <div className="flex-1">
            <div className="font-semibold">Couldn't load this report</div>
            <div className="text-amber-800">{loadError}</div>
            <div className="text-[11px] text-amber-700 mt-1">This usually means your FB isn't connected yet or there's no data for the chosen window. Check Connections, then try again.</div>
          </div>
          <button
            onClick={load}
            data-testid="report-inline-retry"
            className="text-[11px] font-medium px-2 py-1 bg-white border border-amber-300 rounded hover:bg-amber-100"
          >Retry</button>
        </div>
      )}

      {cacheTtl && (
        <div className="mb-2 text-[11px] text-slate-500 font-mono">
          ⓘ Geo breakdown cached for {cacheTtl} min per account. Click Refresh to force regeneration.
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          {dim === "geo" ? <GeoTable rows={rows} loading={loading} /> : <PivotTable rows={rows} dim={dim} loading={loading} />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, positive }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className={`text-xl font-mono mt-1 ${positive === undefined ? "text-slate-900" : positive ? "text-emerald-600" : "text-red-600"}`}>
        {value}
      </div>
    </div>
  );
}

function PivotTable({ rows, dim, loading }) {
  if (!rows.length && !loading) return <Empty dim={dim} />;
  if (loading) return <LoadingRows />;
  return (
    <table className="w-full text-[13px]">
      <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium bg-white border-b border-slate-200">
        <tr>
          <th className="px-3 py-2.5 text-left">{dim === "account" ? "Ad Account" : dim === "vertical" ? "Vertical" : dim === "network" ? "Network" : "Buyer"}</th>
          <th className="px-3 py-2.5 text-right">Live</th>
          <th className="px-3 py-2.5 text-right">Paused</th>
          <th className="px-3 py-2.5 text-right">Spend</th>
          <th className="px-3 py-2.5 text-right">Revenue</th>
          <th className="px-3 py-2.5 text-right">Profit</th>
          <th className="px-3 py-2.5 text-right" title="Revenue / Spend">ROAS</th>
          <th className="px-3 py-2.5 text-right" title="Profit / Spend">ROI</th>
          <th className="px-3 py-2.5 text-right">CPC</th>
          <th className="px-3 py-2.5 text-right" title="Spend / S1 Conv">CPA</th>
          <th className="px-3 py-2.5 text-right" title="Revenue / S1 Conv">RPC</th>
          <th className="px-3 py-2.5 text-right" title="Revenue / Ad Clicks">RPAC</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.map((r) => (
          <tr key={r.group} data-testid={`pivot-row-${r.group}`} className="hover:bg-slate-50">
            <td className="px-3 py-2 font-medium text-slate-900">{r.group}</td>
            <td className="px-3 py-2 text-right font-mono">{r.live_campaigns}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-500">{r.paused_campaigns}</td>
            <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.spend)}</td>
            <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.revenue)}</td>
            <td className={`px-3 py-2 text-right font-mono font-medium ${r.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(r.profit)}</td>
            <td className={`px-3 py-2 text-right font-mono ${r.roas >= 1 ? "text-emerald-600" : "text-red-600"}`}>{(r.roas || 0).toFixed(2)}×</td>
            <td className={`px-3 py-2 text-right font-mono ${r.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(r.roi)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.cpc)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.s1_conversions ? fmtMoney(r.cpa) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.s1_conversions ? fmtMoney(r.rpc) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.clicks ? fmtMoney(r.rpac) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GeoTable({ rows, loading }) {
  if (!rows.length && !loading) return <Empty dim="geo" />;
  if (loading) return <LoadingRows geo />;
  return (
    <table className="w-full text-[13px]">
      <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium bg-white border-b border-slate-200">
        <tr>
          <th className="px-3 py-2.5 text-left">Country</th>
          <th className="px-3 py-2.5 text-right">Spend</th>
          <th className="px-3 py-2.5 text-right">Impressions</th>
          <th className="px-3 py-2.5 text-right">Clicks</th>
          <th className="px-3 py-2.5 text-right">Ad CTR</th>
          <th className="px-3 py-2.5 text-right">CPC</th>
          <th className="px-3 py-2.5 text-right">CPM</th>
          <th className="px-3 py-2.5 text-right">Ad Conv</th>
          <th className="px-3 py-2.5 text-right">CPAA</th>
          <th className="px-3 py-2.5 text-right">Ad CONV%</th>
          <th className="px-3 py-2.5 text-left">Account(s)</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.map((r) => (
          <tr key={r.country} data-testid={`geo-row-${r.country}`} className="hover:bg-slate-50">
            <td className="px-3 py-2 font-medium text-slate-900">{r.country}</td>
            <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.spend)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtInt(r.impressions)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtInt(r.clicks)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.impressions ? fmtPct(r.ad_ctr) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.clicks ? fmtMoney(r.cpc) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.impressions ? fmtMoney(r.cpm) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtInt(r.ad_conversions)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.ad_conversions ? fmtMoney(r.cpaa) : "—"}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-600">{r.clicks ? fmtPct(r.ad_conv_rate) : "—"}</td>
            <td className="px-3 py-2 text-slate-500 text-[11px] font-mono">{(r.ad_accounts || []).join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ dim }) {
  return (
    <div className="py-16 text-center">
      <p className="text-sm text-slate-500">
        {dim === "geo"
          ? "No geo breakdowns yet — launch a campaign and wait for FB to log impressions."
          : "No data for this pivot. Check your date range or launch a campaign."}
      </p>
    </div>
  );
}

function LoadingRows({ geo }) {
  return (
    <div className="py-16 text-center">
      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-indigo-500" />
      <p className="text-xs text-slate-500 font-mono">
        {geo ? "Pulling from FB Insights (breakdowns=country, ~5s)…" : "Aggregating…"}
      </p>
    </div>
  );
}

// ─── Gross Profit time-series ────────────────────────────────────────────
function GrossProfitChart() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/gross-profit?days=${days}`, { timeout: 90000 });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gross profit load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);

  const totals = data?.totals || { spend: 0, revenue: 0, profit: 0, roi: 0, roas: 0 };
  const stats = data?.stats || { profitable_days: 0, unprofitable_days: 0 };
  const profitPositive = totals.profit >= 0;
  const points = data?.points || [];

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="gross-profit-chart">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-indigo-600" /> Gross Profit
          </h2>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Daily FB spend vs. S1 revenue — the one metric that tells you if the business is working.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="gp-range"
            className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs"
          >
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d}d</option>)}
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="gp-refresh"
            className="bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 rounded-md p-1.5"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-5 gap-2 mb-3">
        <MiniStat label="Spend"   value={fmtMoney(totals.spend)}   />
        <MiniStat label="Revenue" value={fmtMoney(totals.revenue)} />
        <MiniStat label="Profit"  value={fmtMoney(totals.profit)} positive={profitPositive} testid="gp-total-profit" />
        <MiniStat label="ROI"     value={fmtPct(totals.roi)}       positive={totals.roi > 0} />
        <MiniStat
          label="Profitable / Unprofitable days"
          value={`${stats.profitable_days} / ${stats.unprofitable_days}`}
          positive={stats.profitable_days > stats.unprofitable_days}
        />
      </div>

      {/* Chart */}
      <div style={{ height: 280 }} data-testid="gp-chart-area">
        {loading ? (
          <div className="h-full flex items-center justify-center text-xs text-slate-500 font-mono">
            <Loader2 className="w-5 h-5 animate-spin mr-2 text-indigo-500" /> Pulling daily spend + revenue…
          </div>
        ) : points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-slate-500 font-mono">
            No data yet — connect Facebook + System1 in Connections.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="profit-pos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="profit-neg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eef2f7" strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#64748b", fontFamily: "JetBrains Mono, monospace" }}
                tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                tickLine={false}
                axisLine={{ stroke: "#e2e8f0" }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#64748b", fontFamily: "JetBrains Mono, monospace" }}
                tickFormatter={(v) => `$${v}`}
                tickLine={false}
                axisLine={{ stroke: "#e2e8f0" }}
                width={55}
              />
              <Tooltip
                contentStyle={{
                  background: "white", border: "1px solid #e2e8f0", borderRadius: "6px",
                  fontSize: "11px", fontFamily: "JetBrains Mono, monospace",
                }}
                formatter={(v, name) => [fmtMoney(v), name]}
                labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, {
                  month: "long", day: "numeric", year: "numeric",
                })}
              />
              <Legend
                wrapperStyle={{ fontSize: "11px", fontFamily: "JetBrains Mono, monospace", paddingTop: 8 }}
                iconType="line"
              />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="2 2" />
              <Area
                type="monotone"
                dataKey="profit"
                stroke="none"
                fill={profitPositive ? "url(#profit-pos)" : "url(#profit-neg)"}
                name="Profit"
                isAnimationActive={false}
              />
              <Line type="monotone" dataKey="spend" stroke="#ef4444" strokeWidth={2} dot={false} name="Spend" isAnimationActive={false} />
              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} name="Revenue" isAnimationActive={false} />
              <Line type="monotone" dataKey="profit" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 3" dot={false} name="Profit" isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, positive, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono truncate">{label}</div>
      <div
        className={`text-sm font-mono font-semibold mt-0.5 ${
          positive === undefined ? "text-slate-900" : positive ? "text-emerald-600" : "text-red-600"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

